/**
 * DailyOmzetChart — omzet per dag: verkoop (lijn) naast uitvoering (staven).
 * Uitvoering = verleden gefactureerd (vol), toekomst ingepland (licht) —
 * zo zie je per dag hoeveel cash er binnenkomt en wat er verkocht is.
 * Data live uit GET /api/kpi/daily (mirror, geen SF). Licht thema, NL-notatie.
 */

import { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useCijfersStore, type DailyOmzet } from '../../stores/cijfers-store';

const RANGES = [
  { id: '2w', label: '2 weken', back: 14, forward: 14 },
  { id: '6w', label: '6 weken', back: 42, forward: 28 },
  { id: '3m', label: '3 maanden', back: 90, forward: 60 },
] as const;

type RangeId = (typeof RANGES)[number]['id'];

const nlEur0 = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const DAY_NAMES = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

function dayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
}

function compactEur(value: number): string {
  if (Math.abs(value) >= 10_000) return `€ ${Math.round(value / 1000)}k`;
  return `€ ${value.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`;
}

interface ChartRow {
  day: string;
  label: string;
  verkoop: number | null;
  gefactureerd: number | null;
  gepland: number | null;
}

function toRows(daily: DailyOmzet): ChartRow[] {
  return daily.days.map((d) => {
    const isFuture = d.day > daily.today;
    return {
      day: d.day,
      label: dayLabel(d.day),
      // Verkoop bestaat alleen in het verleden; toekomstdagen niet als 0 plotten.
      verkoop: isFuture ? null : d.verkoop,
      gefactureerd: d.gefactureerd > 0 ? d.gefactureerd : isFuture ? null : d.gefactureerd,
      gepland: d.gepland > 0 ? d.gepland : null,
    };
  });
}

const SERIES_LABELS: Record<string, string> = {
  verkoop: 'Verkoop',
  gefactureerd: 'Uitvoering (gefactureerd)',
  gepland: 'Uitvoering (ingepland)',
};

export function DailyOmzetChart() {
  const loadDaily = useCijfersStore((s) => s.loadDaily);
  const [rangeId, setRangeId] = useState<RangeId>('6w');
  const [daily, setDaily] = useState<DailyOmzet | null>(null);

  useEffect(() => {
    let active = true;
    const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[1];
    setDaily(null);
    void loadDaily(range.back, range.forward).then((d) => {
      if (active) setDaily(d);
    });
    return () => {
      active = false;
    };
  }, [rangeId, loadDaily]);

  const rows = daily ? toRows(daily) : [];
  const todayLabel = daily ? dayLabel(daily.today) : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <h3 className="text-sm font-medium text-main">Omzet per dag</h3>
          <p className="text-xs text-gray-400">
            Verkoop = verkocht die dag. Uitvoering = gefactureerd; vooruit: ingeplande afleveringen.
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRangeId(r.id)}
              className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                rangeId === r.id
                  ? 'bg-accent/10 text-accent font-medium'
                  : 'text-gray-500 hover:text-main hover:bg-gray-50'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!daily ? (
        <div className="h-64 flex items-center justify-center text-sm text-gray-400">Laden…</div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: '#6b7280', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              minTickGap={20}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={compactEur}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value: number, name: string) => [nlEur0.format(value), SERIES_LABELS[name] ?? name]}
            />
            <Legend
              formatter={(name: string) => (
                <span className="text-xs text-gray-500">{SERIES_LABELS[name] ?? name}</span>
              )}
            />
            {todayLabel && (
              <ReferenceLine
                x={todayLabel}
                stroke="#9ca3af"
                strokeDasharray="4 3"
                label={{ value: 'vandaag', position: 'top', fill: '#9ca3af', fontSize: 10 }}
              />
            )}
            <Bar dataKey="gefactureerd" stackId="uitvoering" fill="var(--brand-accent, #4f46e5)" fillOpacity={0.85} isAnimationActive={false} />
            <Bar dataKey="gepland" stackId="uitvoering" fill="var(--brand-accent, #4f46e5)" fillOpacity={0.3} isAnimationActive={false} />
            <Line
              type="monotone"
              dataKey="verkoop"
              stroke="#0f766e"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
